/**
 * Brouillon local d'un éditeur de lignes (ADR-136 §7) : un long comptage se saisit en plusieurs fois
 * sans objet serveur. Le brouillon vit dans le stockage du navigateur, rangé sous
 * `tenant + identité + commande + paramètres de la feuille` (le local, le comptage recompté) ; il
 * garde les quantités en texte, les autres champs saisis et l'instant de la feuille. Il n'est jamais
 * lu par le serveur ni par une autre personne, et ne devient une quantité qu'à l'envoi de la commande
 * (ERP-STOCK-003). Chaque accès au stockage est entouré d'un `try/catch` : navigation privée, stockage
 * plein ou bloqué, l'écran fonctionne sans brouillon.
 */

export const DRAFT_PREFIX = 'ayumia.line-draft.v1:';

export interface DraftLine {
  id: string;
  /** Données de la ligne (une ligne ajoutée à la main les porte toutes ; une ligne pré-remplie, sa clé). */
  data: Record<string, unknown>;
  entered: Record<string, string>;
  prefilled: boolean;
}

export interface EditorDraft {
  saved_at: string;
  /** Instant de la feuille lue quand le brouillon a été pris. */
  as_of?: string;
  /** Champs de la charge utile saisis (hors lignes et hors valeurs pré-remplies par l'action). */
  values: Record<string, unknown>;
  lines: DraftLine[];
}

export interface DraftStore {
  load(key: string): EditorDraft | null;
  save(key: string, draft: EditorDraft): void;
  clear(key: string): void;
}

export interface DraftKeyParts {
  tenant: string;
  identity: string;
  command: string;
  sheet: Readonly<Record<string, string>>;
}

export function draftKey(parts: DraftKeyParts): string {
  const sheet = Object.keys(parts.sheet)
    .sort()
    .map((name) => `${name}=${parts.sheet[name]}`)
    .join('&');
  return `${DRAFT_PREFIX}${parts.tenant}:${parts.identity}:${parts.command}:${sheet}`;
}

function isDraft(value: unknown): value is EditorDraft {
  if (value === null || typeof value !== 'object') return false;
  const draft = value as Record<string, unknown>;
  return typeof draft['saved_at'] === 'string' && Array.isArray(draft['lines']) && draft['values'] !== null && typeof draft['values'] === 'object';
}

/** Stockage du navigateur ; `storage` absent ou qui lève : aucun brouillon, sans erreur. */
export function browserDraftStore(storage: () => Storage | undefined = () => globalThis.localStorage): DraftStore {
  const area = (): Storage | undefined => {
    try {
      return storage();
    } catch {
      return undefined;
    }
  };
  return {
    load(key) {
      try {
        const raw = area()?.getItem(key);
        if (raw === null || raw === undefined) return null;
        const parsed: unknown = JSON.parse(raw);
        return isDraft(parsed) ? parsed : null;
      } catch {
        return null;
      }
    },
    save(key, draft) {
      try {
        area()?.setItem(key, JSON.stringify(draft));
      } catch {
        // Stockage plein ou refusé : la saisie continue sans brouillon.
      }
    },
    clear(key) {
      try {
        area()?.removeItem(key);
      } catch {
        // Rien à effacer.
      }
    },
  };
}

/** Âge d'un brouillon, en mots (« il y a 12 min »). */
export function draftAge(savedAt: string, now: Date = new Date()): string {
  const saved = Date.parse(savedAt);
  if (Number.isNaN(saved)) return 'à une date inconnue';
  const minutes = Math.max(0, Math.round((now.getTime() - saved) / 60_000));
  if (minutes < 1) return 'à l’instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `il y a ${hours} h`;
  return `il y a ${Math.round(hours / 24)} j`;
}
